import type { ReactElement } from 'react';

import omit from 'lodash/omit';

import style from './Menu.module.css';

import type { NavigationItem } from '../../../NavItem';
import { NavItem } from '../../../NavItem';
import { Text } from '../../../Text';
import type { NavItemClickHandler, NavItemTrackingData } from '../../types';
import type { AppNavItem } from '../types';

interface MenuProps {
  items: AppNavItem[];
  id: string;
  /** A call-to-action to display at the bottom of the menu. */
  cta?: ReactElement | undefined;
  /** An element to display after the list, but in the same container */
  afterMenuListEl?: ReactElement | undefined;
  onNavItemClick?: NavItemClickHandler;
}

export function Menu(props: MenuProps) {
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!props.onNavItemClick) {
      return;
    }

    const target = event.target as HTMLElement;

    // Only track clicks on anchor elements
    const anchorElement = target.closest('a');
    if (!anchorElement) {
      return;
    }

    const label = anchorElement.textContent?.trim();

    const trackingData: NavItemTrackingData = {
      text: label,
      module_name: 'nav_bar_expanded',
      type: 'button',
    };

    props.onNavItemClick(trackingData, event.nativeEvent);
  };

  return (
    <div className={style.menu} onClickCapture={handleMenuClick}>
      <div className={style.menuContainer}>
        {props.items.length > 0 && (
          <div id={props.id} role="list" aria-label="Navigation Menu">
            {props.items
              .map(item => omit(item, 'alwaysVisible') as NavigationItem)
              .map((item, i) => (
                <Text
                  as="div"
                  key={i}
                  variant="heading-22"
                  role="listitem"
                  className={style.listItem}
                >
                  <NavItem
                    className={style.navItem}
                    {...item}
                    orientation="vertical"
                  />
                </Text>
              ))}
          </div>
        )}
        {props.afterMenuListEl && props.afterMenuListEl}
      </div>
      {props.cta && <div className={style.cta}>{props.cta}</div>}
    </div>
  );
}
